import React from "react";

/**
 * Minimal, dependency-free modal used across inventory pages.
 * data-testid conventions:
 *   - Wrapper:   `${testid}-modal`
 *   - Close btn: `${testid}-modal-close`
 */
export default function Modal({ open, onClose, title, children, testid = "modal" }) {
  if (!open) return null;

  return (
    <div
      data-testid={`${testid}-modal`}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="w-full max-w-lg bg-white border border-slate-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2
            style={{ fontFamily: "'Outfit', sans-serif" }}
            className="text-xl font-semibold tracking-tight"
          >
            {title}
          </h2>
          <button
            data-testid={`${testid}-modal-close`}
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export function TextField({ label, testid, hint, error, ...props }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
        {label}
      </span>
      <input
        data-testid={testid}
        className={`mt-2 block w-full h-11 px-3 border ${
          error ? "border-[#D92D20]" : "border-slate-300"
        } bg-white text-sm focus:outline-none focus:border-[#002FA7] transition-colors`}
        {...props}
      />
      {hint && !error && (
        <span className="text-xs text-slate-500 mt-1 block">{hint}</span>
      )}
      {error && (
        <span
          data-testid={`${testid}-error`}
          className="text-xs text-[#D92D20] mt-1 block"
        >
          {error}
        </span>
      )}
    </label>
  );
}

export function TextArea({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
        {label}
      </span>
      <textarea
        data-testid={testid}
        rows={3}
        className="mt-2 block w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:border-[#002FA7] transition-colors"
        {...props}
      />
    </label>
  );
}

export function SelectField({ label, testid, children, ...props }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
        {label}
      </span>
      <select
        data-testid={testid}
        className="mt-2 block w-full h-11 px-3 border border-slate-300 bg-white text-sm focus:outline-none focus:border-[#002FA7] transition-colors"
        {...props}
      >
        {children}
      </select>
    </label>
  );
}

export function PrimaryButton({ testid, children, ...props }) {
  return (
    <button
      data-testid={testid}
      className="h-11 px-6 bg-[#0A0A0B] text-white text-sm font-medium tracking-wide hover:bg-[#002FA7] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      {...props}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ testid, children, ...props }) {
  return (
    <button
      data-testid={testid}
      className="h-11 px-6 border border-slate-300 text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      {...props}
    >
      {children}
    </button>
  );
}
